import { useCallback, useEffect, useRef, useState } from "react";
import type { useDocumentPreview } from "@/components/chat/SidebarChat/DocLink/useDocumentPreview";
import type { WidgetField, WidgetGroup } from "@/domains/buildings/types/buildingWidget";
import type { useToast } from "@/hooks/use-toast";
import type { useStores } from "@/stores";
import type { StateFilter } from "./BuildingDataPageHeader";

function hasKeyboardModifier(event: KeyboardEvent): boolean {
  return event.metaKey || event.ctrlKey || event.altKey;
}

function isTextEntryTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

function canOpenReviewQueue(key: string, canManage: boolean, reviewCount: number): boolean {
  return key.toLowerCase() === "r" && canManage && reviewCount > 0;
}

export function useBuildingDataPageState() {
  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<StateFilter>("all");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const historyTriggerRef = useRef<HTMLButtonElement>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const openedInitialGroupsRef = useRef(false);
  const focusScrollTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const mountSearchInput = useCallback((element: HTMLInputElement | null) => {
    searchRef.current = element;
  }, []);
  const mountContentScroll = useCallback((element: HTMLDivElement | null) => {
    contentScrollRef.current = element;
  }, []);
  return {
    query,
    setQuery,
    activeFilter,
    setActiveFilter,
    historyOpen,
    setHistoryOpen,
    reviewOpen,
    setReviewOpen,
    selectedFieldId,
    setSelectedFieldId,
    inspectorOpen,
    setInspectorOpen,
    activeSection,
    setActiveSection,
    searchRef,
    historyTriggerRef,
    contentScrollRef,
    openedInitialGroupsRef,
    focusScrollTimerRef,
    mountSearchInput,
    mountContentScroll,
  };
}

export type BuildingDataPageState = ReturnType<typeof useBuildingDataPageState>;
type BuildingDataStore = ReturnType<typeof useStores>["buildingDataWidgetStore"];
type Toast = ReturnType<typeof useToast>["toast"];
type DocumentPreviewState = ReturnType<typeof useDocumentPreview>;

function usePreviewError(input: {
  documentPreview: DocumentPreviewState;
  toast: Toast;
  title: string;
}) {
  const { documentPreview, toast, title } = input;
  useEffect(() => {
    if (!documentPreview.error) return;
    toast({ title, description: documentPreview.error, variant: "destructive" });
    documentPreview.clearError();
  }, [documentPreview, documentPreview.error, title, toast]);
}

/**
 * Open every section the first time groups arrive. No field is pre-selected —
 * the inspector stays closed until the user picks a row themselves.
 */
function useInitialSelections(input: {
  groups: WidgetGroup[];
  store: BuildingDataStore;
  state: BuildingDataPageState;
}) {
  const { groups, state, store } = input;
  const { openedInitialGroupsRef } = state;
  useEffect(() => {
    if (openedInitialGroupsRef.current || groups.length === 0) return;
    openedInitialGroupsRef.current = true;
    if (store.openGroupIds.length === 0) groups.forEach((group) => store.openGroup(group.id));
  }, [groups, openedInitialGroupsRef, store]);
}

function useSectionScrollSpy(input: {
  filteredGroups: WidgetGroup[];
  state: BuildingDataPageState;
}) {
  const { filteredGroups, state } = input;
  const { contentScrollRef, setActiveSection } = state;
  useEffect(() => {
    const root = contentScrollRef.current;
    if (!root) return;
    const sections = Array.from(root.querySelectorAll<HTMLElement>("[data-bd-section]"));
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((left, right) => left.boundingClientRect.top - right.boundingClientRect.top)[0];
        const id = visible?.target.getAttribute("data-bd-section");
        if (id) setActiveSection(id);
      },
      { root, rootMargin: "-8% 0px -75% 0px", threshold: [0, 1] }
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [contentScrollRef, filteredGroups, setActiveSection]);
}

function scrollToField(
  focusScrollTimer: BuildingDataPageState["focusScrollTimerRef"],
  contentScrollRef: BuildingDataPageState["contentScrollRef"],
  fieldId: string
) {
  clearTimeout(focusScrollTimer.current);
  focusScrollTimer.current = setTimeout(() => {
    contentScrollRef.current
      ?.querySelector(`[data-bd-field="${CSS.escape(fieldId)}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 250);
}

function usePendingNavigation(input: {
  allFields: WidgetField[];
  groups: WidgetGroup[];
  isLoading: boolean;
  store: BuildingDataStore;
  state: BuildingDataPageState;
  selectField: (field: WidgetField) => void;
}) {
  const { allFields, groups, isLoading, selectField, state, store } = input;
  const { contentScrollRef, focusScrollTimerRef, setActiveFilter, setQuery } = state;
  const pendingFocusFieldId = store.pendingFocusFieldId;
  const pendingFilter = store.pendingFilter;
  useEffect(() => () => clearTimeout(focusScrollTimerRef.current), [focusScrollTimerRef]);
  useEffect(() => {
    if (isLoading) return;
    if (pendingFilter) {
      setQuery("");
      setActiveFilter(pendingFilter);
      store.clearPendingFilter();
      return;
    }
    if (!pendingFocusFieldId) return;
    const field = allFields.find((candidate) => candidate.id === pendingFocusFieldId);
    const group = groups.find((candidate) =>
      candidate.fields.some((candidateField) => candidateField.id === pendingFocusFieldId)
    );
    if (!field || !group) return;
    setQuery("");
    setActiveFilter("all");
    store.openGroup(group.id);
    selectField(field);
    scrollToField(focusScrollTimerRef, contentScrollRef, field.id);
    store.clearFocusField();
  }, [
    allFields,
    contentScrollRef,
    focusScrollTimerRef,
    groups,
    isLoading,
    pendingFilter,
    pendingFocusFieldId,
    selectField,
    setActiveFilter,
    setQuery,
    store,
  ]);
}

function usePageShortcuts(input: {
  canManage: boolean;
  documentPreviewOpen: boolean;
  reviewCount: number;
  state: BuildingDataPageState;
}) {
  const { canManage, documentPreviewOpen, reviewCount, state } = input;
  const { historyOpen, reviewOpen, searchRef, setReviewOpen } = state;
  const modalOpen = reviewOpen || historyOpen || documentPreviewOpen;
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (modalOpen || hasKeyboardModifier(event) || isTextEntryTarget(event.target)) return;
      if (event.key === "/") {
        event.preventDefault();
        searchRef.current?.focus();
      } else if (canOpenReviewQueue(event.key, canManage, reviewCount)) {
        event.preventDefault();
        setReviewOpen(true);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canManage, modalOpen, reviewCount, searchRef, setReviewOpen]);
}

function usePageCleanup(
  store: BuildingDataStore,
  canManage: boolean,
  state: BuildingDataPageState
) {
  const { setReviewOpen } = state;
  useEffect(() => {
    if (!canManage) setReviewOpen(false);
  }, [canManage, setReviewOpen]);
  useEffect(
    () => () => {
      store.clearFocusField();
      store.clearPendingFilter();
    },
    [store]
  );
}

interface BuildingDataPageEffectsInput {
  resources: {
    groups: WidgetGroup[];
    canManage: boolean;
    widgetQuery: { isLoading: boolean };
    documentPreview: DocumentPreviewState;
  };
  derived: {
    allFields: WidgetField[];
    filteredGroups: WidgetGroup[];
    reviewItems: readonly unknown[];
  };
  state: BuildingDataPageState;
  store: BuildingDataStore;
  navigation: { onFieldSelect: (field: WidgetField) => void };
  toast: Toast;
  errorTitle: string;
}

export function useBuildingDataPageEffects(input: BuildingDataPageEffectsInput) {
  const { derived, errorTitle, navigation, resources, state, store, toast } = input;
  usePreviewError({ documentPreview: resources.documentPreview, toast, title: errorTitle });
  useInitialSelections({ groups: resources.groups, store, state });
  useSectionScrollSpy({ filteredGroups: derived.filteredGroups, state });
  usePendingNavigation({
    allFields: derived.allFields,
    groups: resources.groups,
    isLoading: resources.widgetQuery.isLoading,
    store,
    state,
    selectField: navigation.onFieldSelect,
  });
  usePageShortcuts({
    canManage: resources.canManage,
    documentPreviewOpen: resources.documentPreview.isOpen,
    reviewCount: derived.reviewItems.length,
    state,
  });
  usePageCleanup(store, resources.canManage, state);
}
